import {
  useEffect,
  useState
} from 'react';

import {
  fetchAssignmentGroupLabelsForCourse,
  getAssignmentGroupDisplayName,
  renameStudentAssignmentGroup,
  fetchAssignmentGroupDescriptionsForCourse
} from '../../../utils/groupLabels';

function naturalStudentSort(a, b) {
  return String(a?.students?.student_code || '')
    .localeCompare(
      String(b?.students?.student_code || ''),
      undefined,
      {
        numeric: true,
        sensitivity: 'base'
      }
    );
}

export default function AssignmentGroupMembers({
  group,
  title = 'Your Group',
  assignmentId = null,
  courseId = null,
  session = null,
  displayName = '',
  description = ''
}) {
  const members = [...(group?.members || [])]
    .filter(
      member =>
        member?.students?.is_master !== true
    )
    .sort(naturalStudentSort);

  const [label, setLabel] =
    useState(displayName || '');

  const [
    loadedDescription,
    setLoadedDescription
  ] = useState(description || '');

  const [editing, setEditing] =
    useState(false);

  const [draft, setDraft] =
    useState('');

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');

  const groupNumber =
    group?.groupNumber ?? null;

  useEffect(() => {
    setLabel(displayName || '');
  }, [displayName]);

  useEffect(() => {
    let cancelled = false;

    if (
      !assignmentId ||
      !courseId ||
      !groupNumber
    ) {
      return undefined;
    }

    fetchAssignmentGroupLabelsForCourse(
      courseId
    )
      .then(labels => {
        if (cancelled) return;

        setLabel(
          labels?.[
            `${assignmentId}|${groupNumber}`
          ] || ''
        );
      })
      .catch(err => {
        console.error(
          'Loading Assignment Group name failed:',
          err
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    assignmentId,
    courseId,
    groupNumber
  ]);

  useEffect(() => {
    let cancelled = false;

    if (description) {
      setLoadedDescription(description);

      return () => {
        cancelled = true;
      };
    }

    setLoadedDescription('');

    if (
      !assignmentId ||
      !courseId ||
      !groupNumber
    ) {
      return undefined;
    }

    fetchAssignmentGroupDescriptionsForCourse(
      courseId
    )
      .then(descriptions => {
        if (cancelled) return;

        setLoadedDescription(
          descriptions?.[
            `${assignmentId}|${groupNumber}`
          ] || ''
        );
      })
      .catch(err => {
        console.error(
          'Loading Assignment Group description failed:',
          err
        );
      });

    return () => {
      cancelled = true;
    };
  }, [
    assignmentId,
    courseId,
    groupNumber,
    description
  ]);


  if (!group || members.length === 0) {
    return null;
  }

  const groupDisplayName =
    displayName ||
    getAssignmentGroupDisplayName(
      label
        ? {
            [`${assignmentId}|${groupNumber}`]:
              label
          }
        : {},
      assignmentId,
      groupNumber
    );

  const canRename =
    Boolean(
      assignmentId &&
      groupNumber &&
      session &&
      session.isMaster !== true
    );

  function beginRename() {
    setDraft(label || '');
    setError('');
    setEditing(true);
  }

  async function saveRename(event) {
    event.preventDefault();

    setSaving(true);
    setError('');

    try {
      const result =
        await renameStudentAssignmentGroup({
          session,
          courseAssignmentId:
            assignmentId,
          groupNumber,
          name: draft
        });

      setLabel(
        String(
          result?.name || ''
        ).trim()
      );

      setEditing(false);
    } catch (err) {
      setError(
        err?.message ||
        'The Group could not be renamed.'
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section style={styles.section}>
      <div style={styles.heading}>
        <div>
          <div>
            {title}
            {groupNumber
              ? ` — ${groupDisplayName}`
              : ''}
          </div>

          {loadedDescription && (
            <div style={styles.description}>
              {loadedDescription}
            </div>
          )}
        </div>

        {canRename && !editing && (
          <button
            type="button"
            style={styles.renameBtn}
            onClick={beginRename}
          >
            Rename
          </button>
        )}
      </div>

      {editing && (
        <form
          style={styles.renameForm}
          onSubmit={saveRename}
        >
          <input
            value={draft}
            maxLength={80}
            autoFocus
            placeholder={`Group ${groupNumber}`}
            onChange={event =>
              setDraft(event.target.value)
            }
            style={styles.renameInput}
          />

          <button
            disabled={saving}
            style={styles.saveBtn}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>

          <button
            type="button"
            disabled={saving}
            style={styles.cancelBtn}
            onClick={() =>
              setEditing(false)
            }
          >
            Cancel
          </button>
        </form>
      )}

      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>English Name</th>
              <th style={styles.th}>Chinese Name</th>
            </tr>
          </thead>
          <tbody>
            {members.map(member => {
              const student =
                member.students || {};

              return (
                <tr key={member.id}>
                  <td style={styles.td}>
                    {student.student_code || '—'}
                  </td>
                  <td style={styles.td}>
                    {student.english_name || '—'}
                  </td>
                  <td style={styles.td}>
                    {student.chinese_name || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const styles = {
  section: {
    marginTop: '20px',
    border: '1px solid #d7dee5',
    borderRadius: '10px',
    background: '#fff',
    overflow: 'hidden'
  },

  heading: {
    padding: '11px 13px',
    background: '#f7f9fa',
    borderBottom: '1px solid #d7dee5',
    color: '#1f2933',
    fontSize: '14px',
    fontWeight: '800',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '10px'
  },

  description: {
    marginTop: '2px',
    color: '#66788a',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: 1.35
  },

  renameBtn: {
    border: '1px solid #bcccdc',
    borderRadius: '5px',
    background: '#fff',
    color: '#42515f',
    padding: '3px 7px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  renameForm: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '9px 13px',
    borderBottom: '1px solid #e3e9ee',
    flexWrap: 'wrap'
  },

  renameInput: {
    minWidth: '180px',
    flex: 1,
    border: '1px solid #bcccdc',
    borderRadius: '5px',
    padding: '6px 8px',
    fontSize: '13px'
  },

  saveBtn: {
    border: 0,
    borderRadius: '5px',
    background: '#216e45',
    color: '#fff',
    padding: '6px 9px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },

  cancelBtn: {
    border: '1px solid #bcccdc',
    borderRadius: '5px',
    background: '#fff',
    color: '#42515f',
    padding: '5px 9px',
    fontSize: '12px',
    cursor: 'pointer'
  },

  error: {
    padding: '7px 13px',
    color: '#b42318',
    fontSize: '12px',
    borderBottom: '1px solid #e3e9ee'
  },

  tableWrap: {
    width: '100%',
    overflowX: 'auto'
  },

  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '460px'
  },

  th: {
    textAlign: 'left',
    padding: '8px 10px',
    fontSize: '12px',
    color: '#42515f',
    background: '#fbfcfd',
    borderBottom: '1px solid #e3e9ee'
  },

  td: {
    padding: '9px 10px',
    fontSize: '13px',
    color: '#1f2933',
    borderBottom: '1px solid #edf1f4'
  }
};
